import { getDpdpPolicies } from "./dpdpPolicies";

/** Gap assessment section → DPDP Policy Store templates that address the gap area. */
export const GAP_SECTION_POLICY_IDS = {
  consent: ["consent-management"],
  notice: ["privacy-notice"],
  rights: ["data-principal-rights"],
  fiduciary: ["personal-data-protection"],
  breach: ["breach-notification"],
  crossborder: ["cross-border-transfer"],
  governance: ["personal-data-protection"],
};

/** Question-level overrides for more specific policy matches. */
export const GAP_QUESTION_POLICY_IDS = {
  CON07: ["children-data"],
  DPR03: ["retention-erasure"],
  DPR04: ["grievance-redressal"],
  DFO04: ["vendor-processor"],
  DFO05: ["vendor-processor"],
  DFO07: ["personal-data-protection"],
  GOV05: ["vendor-processor"],
  GOV06: ["retention-erasure"],
  GOV07: ["personal-data-protection", "grievance-redressal"],
  CBT03: ["vendor-processor"],
};

export function isGapResponse(answer) {
  return answer === "No" || answer === "Partially";
}

export function getPolicyIdsForQuestion(sectionId, questionId) {
  const ids = new Set();
  (GAP_SECTION_POLICY_IDS[sectionId] || []).forEach((id) => ids.add(id));
  (GAP_QUESTION_POLICY_IDS[questionId] || []).forEach((id) => ids.add(id));
  return [...ids];
}

/**
 * Collect unique policy recommendations for all gap answers (No / Partially).
 * storeIndex comes from GET /policy-store/recommendation-index. When a stored
 * file exists for a catalogue key, that file is what the recommendation names.
 */
export function collectGapPolicyRecommendations(
  answers,
  sections,
  organizationName,
  storeIndex,
) {
  const allPolicies = getDpdpPolicies({ organizationName });
  const policyById = Object.fromEntries(allPolicies.map((p) => [p.id, p]));
  const byKey = storeIndex?.by_key || {};
  const byId = storeIndex?.by_id || {};
  const policyMap = new Map();

  const addRecommendation = (mapKey, policy, question) => {
    if (!policy || !mapKey) return;
    if (!policyMap.has(mapKey)) {
      policyMap.set(mapKey, { policy, gapQuestions: [] });
    }
    policyMap.get(mapKey).gapQuestions.push({
      id: question.id,
      text: question.text,
      sectionTitle: question.sectionTitle,
      answer: question.answer,
    });
  };

  sections.forEach((section) => {
    section.questions.forEach((q) => {
      const ans = answers[q.id];
      if (!isGapResponse(ans)) return;

      const keys =
        Array.isArray(q.policy_keys) && q.policy_keys.length
          ? q.policy_keys
          : getPolicyIdsForQuestion(section.id, q.id);
      const gapQuestion = {
        id: q.id,
        text: q.text,
        sectionTitle: section.title,
        answer: ans,
      };

      keys.forEach((policyId) => {
        const catalogue = policyById[policyId];
        const stored = byKey[policyId];
        if (!catalogue && !stored) return;
        addRecommendation(
          policyId,
          {
            id: policyId,
            title: stored?.title || catalogue.title,
            subtitle: catalogue?.subtitle || stored?.description || "",
            actRef: stored?.act_ref || catalogue?.actRef || "",
            documentId: stored?.id || null,
            fileRevision: stored?.file_revision || null,
            source: stored?.source || null,
            isDefault: stored ? Boolean(stored.is_default) : false,
          },
          gapQuestion,
        );
      });

      (q.policy_document_ids || []).forEach((docId) => {
        const stored = byId[docId];
        if (!stored) return;
        const mapKey = stored.catalogue_key || `doc:${stored.id}`;
        if (policyMap.has(mapKey)) return;
        addRecommendation(
          mapKey,
          {
            id: stored.catalogue_key || null,
            title: stored.title,
            subtitle: stored.description || "Stored organisation policy",
            actRef: stored.act_ref || "",
            documentId: stored.id,
            fileRevision: stored.file_revision || null,
            source: stored.source || null,
            isDefault: Boolean(stored.is_default),
          },
          gapQuestion,
        );
      });
    });
  });

  return [...policyMap.values()];
}

export function collectSectionGapPolicyRecommendations(
  answers,
  section,
  organizationName,
) {
  if (!section) return [];
  const all = collectGapPolicyRecommendations(answers, [section], organizationName);
  return all;
}
